import { useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { PlayArrow, Replay } from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import type { AdminJobDto } from '@api/types/adminJobs'
import type { StatisticsJobDto } from '@api/types/statistics'
import { useAdminJobs, useJobHistory } from '../hooks/useAdminJobs'
import { formatDuration, jobStatusLabel, jobTypeLabel, parseMetadata, summarize } from '../popularTimes'
import { JobRunStatus } from './JobRunStatus'
import { RunJobDialog } from './RunJobDialog'

const formatDate = (s?: string | null) => {
  if (!s) return '-'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString()
}

/** History job types the runner does not launch, named for people. */
const EXTRA_NAMES: Record<string, string> = {
  DailyStatisticsAggregation: 'Daily statistics (scheduled)',
}

interface Launch {
  jobId: string
  name: string
}

/**
 * Statistics → Job history: a "Run now" list of every job that can safely be started on demand (confirm, running
 * state, one at a time per lock group), then the history itself with who launched what, with which parameters,
 * how it ended. "Run again" reopens a history row's job with its parameters.
 */
export function JobHistoryTab() {
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const jobs = useAdminJobs()
  const history = useJobHistory(page, pageSize)
  const [dialog, setDialog] = useState<{ job: AdminJobDto; parameters?: Record<string, unknown> | null } | null>(null)
  const [launches, setLaunches] = useState<Launch[]>([])

  const runNow = useMemo(() => (jobs.data ?? []).filter((j) => j.showInRunNow), [jobs.data])
  const busyGroups = useMemo(() => new Set((jobs.data ?? []).filter((j) => j.isRunning).map((j) => j.lockGroup)), [jobs.data])
  const names = useMemo(() => {
    const out: Record<string, string> = { ...EXTRA_NAMES }
    for (const j of jobs.data ?? []) out[j.jobType] = j.name
    return out
  }, [jobs.data])
  const jobForType = (jobType: string) => runNow.find((j) => j.historyJobTypes.includes(jobType) && j.jobType === jobType)
    ?? runNow.find((j) => j.historyJobTypes.includes(jobType))

  const jobColumns: DataTableColumn<AdminJobDto>[] = [
    {
      key: 'name',
      header: 'Job',
      primary: true,
      render: (j) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{j.name}</Typography>
          <Typography variant="caption" color="text.secondary">{j.description}</Typography>
        </Box>
      ),
    },
    {
      key: 'state',
      header: 'State',
      width: 120,
      render: (j) => j.isRunning
        ? <Chip size="small" color="info" label="Running" />
        : busyGroups.has(j.lockGroup)
          ? <Chip size="small" variant="outlined" label="Waiting (lock busy)" />
          : <Chip size="small" variant="outlined" label="Idle" />,
    },
    {
      key: 'last',
      header: 'Last run',
      hideBelow: 'md',
      render: (j) => j.lastRun
        ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color={jobStatusLabel(j.lastRun.status).color} label={jobStatusLabel(j.lastRun.status).label} />
            <Typography variant="caption" color="text.secondary">{formatDate(j.lastRun.startedAt ?? j.lastRun.createdAt)}</Typography>
          </Stack>
        )
        : <Typography variant="caption" color="text.secondary">Never</Typography>,
    },
  ]

  const historyColumns: DataTableColumn<StatisticsJobDto>[] = [
    {
      key: 'started',
      header: 'Started',
      width: 170,
      render: (r) => <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.startedAt ?? r.createdAt)}</Typography>,
    },
    {
      key: 'job',
      header: 'Job',
      primary: true,
      render: (r) => {
        const meta = parseMetadata(r)
        const params = summarize(meta?.parameters) || (r.dateRangeStart && r.dateRangeEnd ? `${r.dateRangeStart} to ${r.dateRangeEnd}` : '')
        return (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">{jobTypeLabel(r.jobType, names)}</Typography>
            {params && <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{params}</Typography>}
          </Box>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: 110,
      render: (r) => <Chip size="small" color={jobStatusLabel(r.status).color} label={jobStatusLabel(r.status).label} />,
    },
    { key: 'duration', header: 'Duration', width: 90, render: (r) => formatDuration(r.duration) },
    { key: 'records', header: 'Records', align: 'right', width: 90, hideBelow: 'md', render: (r) => r.recordsProcessed.toLocaleString() },
    {
      key: 'result',
      header: 'Result',
      hideBelow: 'lg',
      render: (r) => r.errorMessage
        ? <Typography variant="caption" color="error.main" sx={{ wordBreak: 'break-word' }}>{r.errorMessage}</Typography>
        : <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{summarize(parseMetadata(r)?.result)}</Typography>,
    },
    {
      key: 'by',
      header: 'Triggered by',
      hideBelow: 'md',
      render: (r) => <Typography variant="body2" noWrap>{r.triggeredByUserName || (r.triggeredByUserId ? 'Admin' : 'Schedule')}</Typography>,
    },
  ]

  const onStarted = (jobId: string, job: AdminJobDto) => setLaunches((l) => [{ jobId, name: job.name }, ...l.filter((x) => x.jobId !== jobId)].slice(0, 5))

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Run now</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Jobs that are safe to start on demand. Each launch is confirmed, recorded below, and refused while the same job (or one sharing its lock) is running.
      </Typography>
      {launches.length > 0 && (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {launches.map((l) => (
            <JobRunStatus key={l.jobId} jobId={l.jobId} title={l.name} onClose={() => setLaunches((all) => all.filter((x) => x.jobId !== l.jobId))} />
          ))}
        </Stack>
      )}
      {jobs.error && <Alert severity="error" sx={{ mb: 2 }}>Could not load the job list.</Alert>}
      <DataTable
        rows={runNow}
        columns={jobColumns}
        getRowKey={(j) => j.key}
        loading={jobs.isLoading}
        emptyMessage="No jobs can be launched."
        page={0}
        pageSize={50}
        onPageChange={() => undefined}
        onPageSizeChange={() => undefined}
        pageSizeOptions={[50]}
        rowActions={(j) => (
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrow />}
            disabled={j.isRunning || busyGroups.has(j.lockGroup)}
            onClick={() => setDialog({ job: j })}
          >
            Run now
          </Button>
        )}
      />

      <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 1 }}>History</Typography>
      <DataTable
        rows={history.data?.logs ?? []}
        columns={historyColumns}
        getRowKey={(r) => r.id}
        loading={history.isLoading}
        error={history.error ? 'Could not load the job history.' : null}
        emptyMessage="No job history yet."
        page={page}
        pageSize={pageSize}
        total={history.data?.totalCount ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPageSize(s); setPage(0) }}
        pageSizeOptions={[10, 25, 50]}
        rowActions={(r) => {
          const job = jobForType(r.jobType)
          if (!job) return null
          return (
            <Tooltip title={`Run ${job.name} again`}>
              <span>
                <IconButton
                  size="small"
                  aria-label={`Run ${job.name} again`}
                  disabled={job.isRunning || busyGroups.has(job.lockGroup)}
                  onClick={() => setDialog({ job, parameters: parseMetadata(r)?.parameters })}
                >
                  <Replay fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )
        }}
      />

      {dialog && (
        <RunJobDialog job={dialog.job} initialParameters={dialog.parameters} onClose={() => setDialog(null)} onStarted={onStarted} />
      )}
    </Box>
  )
}
